import React from 'react';
import { Link } from 'react-router-dom';

import {
  BASE_CLASSES,
  RADIUS_CLASS,
  VARIANT_CLASSES,
  SIZE_CLASSES,
  ICON_SIZE_CLASSES,
} from './Button';

import type { LinkButtonProps } from './Button.types';

/**
 * LinkButton - A Link component styled as a button
 *
 * Use this for navigation that should look like a button.
 * Renders as an anchor tag (<a>) for correct semantics.
 *
 * @example
 * ```tsx
 * <LinkButton to="/signup" variant="primary" size="lg">
 *   Get Started
 * </LinkButton>
 * ```
 */
export function LinkButton({
  children,
  to,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  ...props
}: LinkButtonProps): React.JSX.Element {
  const variantClasses = VARIANT_CLASSES[variant];
  const sizeClasses = SIZE_CLASSES[size];
  const iconSizeClasses = ICON_SIZE_CLASSES[size];

  return (
    <Link
      to={to}
      className={`
        ${BASE_CLASSES}
        ${RADIUS_CLASS}
        ${variantClasses}
        ${sizeClasses}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `
        .replace(/\s+/g, ' ')
        .trim()}
      {...props}
    >
      {leftIcon && (
        <span
          className={`inline-flex shrink-0 items-center justify-center ${iconSizeClasses}`}
        >
          {leftIcon}
        </span>
      )}
      <span className="flex items-center">{children}</span>
      {rightIcon && (
        <span
          className={`inline-flex shrink-0 items-center justify-center ${iconSizeClasses}`}
        >
          {rightIcon}
        </span>
      )}
    </Link>
  );
}

export default LinkButton;
